import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid, Text } from 'elements';
import moment, { Moment } from 'moment';
import EditorComponent from 'components/base/EditorComponent';
import { showAlert } from 'components/modal/showConfirm';
import { saveLibrary, getLibraryCompany, getLibraryItem } from 'libs/api/case';
import { trim, getUrlQuery } from 'libs/utils';
import { CWindowWidth, CMonthFormat } from 'constants/base.const';
import { RegionTypes, EmployedTypes } from 'enums/base.enum';
import { Button, Select, DatePicker, Input } from 'antd';
import { Colors } from 'GlobalStyles';
import MainBackImage from 'assets/images/banners/4.webp';

const LibraryWrite = () => {
  const location = useLocation();
  const { id } = getUrlQuery(location.search);
  const history = useHistory();
  const [companies, setCompanies] = useState<Array<{ id: number; value: string }>>();
  const [company, setCompany] = useState<string>('');
  const [region, setRegion] = useState<string>(RegionTypes[0].value);
  const [selectedType, setSelectedType] = useState(EmployedTypes[0].value);
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [title, setTitle] = useState<string>('');
  const [body, setBody] = useState<string>('');

  const getLibrary = async () => {
    const { success, data } = await getLibraryItem(id);
    if (success) {
      setTitle(data.title);
      setCompany(data.company?.id);
      setRegion(data.location);
      setSelectedType(data.hired_type);
      setSelectedDate(moment(data.interview_date));
      setBody(data.body);
    }
  };

  const getCompanies = async () => {
    const { success, data } = await getLibraryCompany();
    if (success) {
      setCompanies(data);
      if (!id) setCompany(data[0].id);
    }
  };

  const checkForm = () => {
    if (!selectedDate) {
      showAlert(undefined, '기출문제 글쓰기', '기출 시기를 선택해주세요.');
      return false;
    }
    const trimedTitle = trim(title);
    if (!trimedTitle) {
      showAlert(undefined, '해설 쓰기', '제목을 입력해주세요.');
      return false;
    }
    const trimedBody = trim(body);
    if (!trimedBody) {
      showAlert(undefined, '기출문제 글쓰기', '내용을 입력해주세요.');
      return false;
    }
    return {
      title: trimedTitle,
      body: trimedBody,
      company_id: company,
      hired_type: selectedType,
      interview_date: selectedDate.format('YYYY-MM') + '-01',
      location: region,
    };
  };

  const save = async () => {
    const params = checkForm();
    if (params) {
      const { success } = await saveLibrary(params, id ? Number(id) : undefined);
      if (success) history.push('/case/library');
    }
  };

  useEffect(() => {
    getCompanies();
    if (id) getLibrary();
  }, []);

  return (
    <>
      <Grid image={MainBackImage} height='400px' md={{ height: '100px' }}>
        <Grid height='100%' backgroundColor={`${Colors.black}33`} justify='center'>
          <Grid width={`${CWindowWidth}px`} padding='0 7%' align='center'>
            <Grid>
              <Text color={Colors.white} size='4rem' bold md={{ size: '2rem' }}>
                Case 기출 라이브러리
              </Text>
            </Grid>
            <Grid marginTop='5px'>
              <Text color={Colors.white} size='1.1rem' md={{ size: '0.9rem' }}>
                Case 인터뷰 실제 기출문제 게시판입니다.
              </Text>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid justify='center'>
        <Grid width={`${CWindowWidth}px`} padding='38px 20px 150px 20px'>
          <Grid>
            <Text size='2rem'>기출문제 글쓰기</Text>
          </Grid>
          <Grid marginTop='32px'>
            <Text size='1.3rem'>Case 정보:</Text>
          </Grid>
          <Grid marginTop='14px' gap='40px' align='center'>
            <Text bold>회사</Text>
            <Select placeholder='회사를 선택해주세요.' value={company} onChange={setCompany}>
              {companies &&
                companies.map((company) => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.value}
                  </Select.Option>
                ))}
            </Select>
            <Text bold>지역</Text>
            <Select placeholder='지역을 선택해주세요.' value={region} onChange={setRegion}>
              {RegionTypes.map((type) => (
                <Select.Option value={type.value} key={type.value}>
                  {type.text}
                </Select.Option>
              ))}
            </Select>
          </Grid>
          <Grid marginTop='14px' gap='40px' align='center'>
            <Text bold>구분</Text>
            <Grid isInline gap='15px'>
              {EmployedTypes.map((type, index) => (
                <Button
                  key={index}
                  type={selectedType === type.value ? 'primary' : undefined}
                  onClick={() => setSelectedType(type.value)}
                >
                  {type.text}
                </Button>
              ))}
            </Grid>
          </Grid>
          <Grid marginTop='14px' gap='40px' align='center'>
            <Text bold>기출 시기</Text>
            <DatePicker
              picker='month'
              format={CMonthFormat}
              placeholder='시작일'
              style={{ backgroundColor: '#f0f8f6', border: 0, borderRadius: 8 }}
              value={selectedDate}
              onChange={(value) => setSelectedDate(value)}
            />
          </Grid>
          <Grid marginTop='50px'>
            <Text size='1.1rem'>제목</Text>
          </Grid>
          <Grid marginTop='5px'>
            <Input
              size='large'
              placeholder='제목을 입력해주세요'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>
          <Grid marginTop='25px'>
            <EditorComponent content={body} onChange={setBody} apiPath='/library/image/' />
          </Grid>
          <Grid justify='flex-end' marginTop='50px'>
            <Grid isInline gap='20px'>
              <Button size='large' onClick={() => history.push('/case/library')}>
                취소
              </Button>
              <Button type='primary' size='large' onClick={save}>
                {id ? '수정' : '등록'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default LibraryWrite;
